import { useParams } from 'react-router-dom';
import { useAdvertiserDetail, useAdminBookings } from '@/api/ads/hooks';
import { ResourceGate } from '@/features/ResourceGate';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Stat } from '@/components/ui/stat';
import { Badge } from '@/components/ui/badge';
import { Table, Th, Td, EmptyRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { npr, fmtDate } from '@/lib/format';

export function AdvertiserDetailPage() {
  const { id } = useParams();
  const advId = id ? Number(id) : undefined;
  const adv = useAdvertiserDetail(advId);
  const bookings = useAdminBookings({ advertiser: advId });

  if (adv.isLoading) return <Spinner label="Loading advertiser…" />;
  if (!adv.data) return <p className="text-muted-foreground">Advertiser not found.</p>;

  const a = adv.data;
  return (
    <ResourceGate slug="ads.advertisers">
      <PageHeader title={a.name} description={a.company || a.email || ''} action={<Badge status={a.status}>{a.status}</Badge>} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Wallet balance" value={a.wallet ? npr(a.wallet.balance) : '—'} />
        <Stat label="Held" value={a.wallet ? npr(a.wallet.held) : '—'} />
        <Stat label="Available" value={a.wallet ? npr(a.wallet.available) : '—'} />
        <Stat label="Campaigns" value={String(a.campaigns_count)} />
      </div>

      <Card className="mt-5">
        <CardHeader title="Bookings" />
        <CardBody className="p-0">
          {bookings.isLoading ? (
            <div className="p-6">
              <Spinner label="Loading bookings…" />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th>Dates</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {(bookings.data?.results.length ?? 0) === 0 && <EmptyRow colSpan={4}>No bookings.</EmptyRow>}
                {bookings.data?.results.map((b) => (
                  <tr key={b.id}>
                    <Td className="font-medium">{b.product_name}</Td>
                    <Td className="text-muted-foreground">
                      {fmtDate(b.start_date)} → {fmtDate(b.end_date)}
                    </Td>
                    <Td>{npr(b.total_amount)}</Td>
                    <Td>
                      <Badge status={b.status}>{b.status_display}</Badge>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </ResourceGate>
  );
}
